import { Chip, Css, HbLoadingSpinner, Icon, Palette, TestIds, Tooltip, useTestIds } from "@homebound/beam";
import { useState } from "react";
import { MaterialVariantsForPlaceholderTliFilter, Order } from "src/generated/graphqlTypes";
import {
  PlaceholderProductSearch_MaterialVariantFragment,
  PlaceholderProductSearch_PossibleMaterialsQuery,
  usePlaceholderProductSearch_PossibleMaterialsQuery,
  usePlaceholderProductSearch_RequirementsTliQuery,
} from "src/routes/libraries/design-catalog/components/PlaceholderProductSearch.generated";
import { PRODUCT_FALLBACK_IMG_URL } from "src/routes/libraries/product-catalog/components/product-images-viewer/ProductImageViewer";
import { fail } from "src/utils";
import { ProductSearchFilters } from "./ProductSearchFilters";

export type PlaceholderMaterialSelection =
  | { kind: "materials"; ids: string[] }
  | { kind: "noMaterialVariant" }
  | undefined;
type OnMaterialSelected = (
  selection:
    | { kind: "material"; id: string; mv: PlaceholderProductSearch_MaterialVariantFragment }
    | { kind: "noMaterialVariant" },
) => void;

// Export for testing
export type PlaceholderProductSearchProps = {
  placeholderTliId: string;
  /** Used to highlight the currently-selected MaterialVariants (or noMaterialVariant). */
  currentMaterials: PlaceholderMaterialSelection;
  onMaterialSelected: OnMaterialSelected;
  showNoMaterialVariant: boolean;
};

/**
 * Provides "what products can fit in this placeholder?".
 *
 * This component is used both by the "Edit Personalization Option" within a modal, and the
 * "Design Package Configurator" as a standalone page.
 */
export function PlaceholderProductSearch(props: PlaceholderProductSearchProps) {
  const { placeholderTliId, onMaterialSelected, currentMaterials, showNoMaterialVariant } = props;
  // Pull in the Placeholder requirements (like `36in Gas Range`) and possible Attribute Filters (like Finish, Installation Type, etc.)
  const query = usePlaceholderProductSearch_RequirementsTliQuery({
    variables: { placeholderTliId },
  });
  const tid = useTestIds({}, "PlaceholderProductSearch");
  const [filter, setFilter] = useState<MaterialVariantsForPlaceholderTliFilter>({
    takeoffLineItemId: placeholderTliId,
  });
  const { data, loading: isLoadingPossibilities } = usePlaceholderProductSearch_PossibleMaterialsQuery({
    variables: { filter, orderBy: { lastUsedAt: Order.Desc } },
  });

  const placeholderTli = query.data?.takeoffLineItem;
  if (!placeholderTli) {
    return <HbLoadingSpinner />;
  }
  const requirementMavs = placeholderTli.materialVariant?.materialAttributeValues ?? [];
  const materialVariants = data?.materialVariantsForPlaceholderTli ?? [];

  return (
    <>
      <div css={Css.df.fdc.gap2.$}>
        <div css={Css.df.jcsb.aie.$}>
          <div css={Css.df.aic.gap2.$}>
            <h1 css={Css.xl.$}>{placeholderTli.item.name}</h1>
            {!isLoadingPossibilities && (
              <span css={Css.sm.gray700.$}>{`${materialVariants.length} results found`}</span>
            )}
          </div>
        </div>
        <div css={Css.df.gap1.mb1.$}>
          {requirementMavs.map((mav) => (
            <Chip key={mav.id} text={`${mav.dimension.name}: ${mav.textValue}`} compact />
          ))}
        </div>
      </div>
      <ProductSearchFilters
        placeholderTliId={placeholderTliId}
        placeholderMv={placeholderTli.materialVariant! ?? fail("placeholders should always have a material variant")}
        placeholderItem={placeholderTli.item}
        setFilter={setFilter}
      />
      <div css={Css.df.fdc.gap2.md.$}>
        {isLoadingPossibilities ? (
          <HbLoadingSpinner />
        ) : (
          <div css={Css.dg.gtc("repeat(auto-fill, 275px)").gar("390px").gap2.$}>
            {showNoMaterialVariant && (
              <NoProductCard tid={tid} currentMaterial={currentMaterials} onMaterialSelected={onMaterialSelected} />
            )}
            {materialVariants.map((mv) => (
              <MaterialCard
                key={mv.id}
                tid={tid}
                currentMaterial={currentMaterials}
                onMaterialSelected={onMaterialSelected}
                mv={mv}
              />
            ))}
          </div>
        )}
      </div>
    </>
  );
}

/** Shows a card that means "no product is assigned to this slot". */
function NoProductCard(props: {
  tid: TestIds;
  currentMaterial: PlaceholderMaterialSelection;
  onMaterialSelected: OnMaterialSelected;
}) {
  return (
    <div
      /* The "Empty Product Slot" row/card/selection */
      css={{
        ...Css.bgWhite.hPx(350).bshBasic.df.fdc.gap2.p2.aic.cursorPointer.br4.$,
        ...Css.onHover.bshHover.bgGray50.$,
        // if no-selection is the currently-assigned option, highlight it
        ...Css.if(props.currentMaterial?.kind === "noMaterialVariant").ba.bcBlue200.bgBlue50.$,
      }}
      onClick={() => props.onMaterialSelected({ kind: "noMaterialVariant" })}
      {...props.tid.noProductCard}
    >
      <div /* pseudo-img */ css={Css.h100.w100.objectCover.oh.$}>
        <div css={Css.h100.w100.df.fdc.aic.jcc.gap1.tac.ba.bcGray500.bgGray50.gray700.br4.$}>
          <Icon icon="remove" color={Palette.Gray700} inc={4} />
          <div css={Css.mdSb.df.fdc.aic.$}>
            Make this an <br />
            empty Product Slot
            <Tooltip
              title={`
                Sometimes a product type isn't offered with certain Package Options (e.g., no refrigerator
                with the Essential package). In these cases, tell Design Packages to leave it empty!
              `}
            >
              <Icon icon="infoCircle" color={Palette.Gray700} inc={2} />
            </Tooltip>
          </div>
        </div>
      </div>
    </div>
  );
}

function MaterialCard(props: {
  tid: TestIds;
  currentMaterial: PlaceholderMaterialSelection;
  onMaterialSelected: OnMaterialSelected;
  mv: PlaceholderProductSearch_PossibleMaterialsQuery["materialVariantsForPlaceholderTli"][number];
}) {
  const { tid, currentMaterial, onMaterialSelected, mv } = props;
  return (
    <div
      css={{
        ...Css.bgWhite.br8.p2.df.fdc.gap2.relative.hPx(350).cursorPointer.bshBasic.onHover.bshHover.$,
        // if this BI is the currently-assigned BI, highlight it
        ...Css.if(currentMaterial?.kind === "materials" && currentMaterial.ids.includes(mv.id)).ba.bcBlue200.bgBlue50.$,
      }}
      onClick={() => onMaterialSelected({ kind: "material", id: mv.id, mv })}
      {...tid.materialCard}
    >
      <div css={Css.relative.hPx(250).maxhPx(250).f1.br4.$}>
        <img
          alt={`${mv.displayName}-product`}
          src={mv.featuredImage?.asset?.previewUrl ?? PRODUCT_FALLBACK_IMG_URL}
          css={Css.absolute.h100.w100.p1.br4.objectCover.$}
        />
      </div>
      <Tooltip title={mv.displayName}>
        <div css={Css.df.fdc.gap1.oh.$}>
          <div css={Css.sm.lineClamp(2).$} {...tid.displayName}>
            {mv.displayName}
          </div>
        </div>
      </Tooltip>
    </div>
  );
}
